import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ConfirmDialog from '../../../components/ui/ConfirmDialog'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../../lib/useMediaQuery'
import { useTableSort } from '../../../lib/useTableSort'
import MeetingManagementCards from '../../meetingManagement/components/MeetingManagementCards'
import MeetingManagementTable from '../../meetingManagement/components/MeetingManagementTable'
import ReopenTasksDialog from '../../meetingManagement/components/ReopenTasksDialog'
import TaskDecisionButtons from '../../meetingManagement/components/TaskDecisionButtons'
import TaskDetailDialog, {
  type TaskDetailTarget,
} from '../../meetingManagement/components/TaskDetailDialog'
import TaskFilesDialog, {
  type TaskFilesTarget,
} from '../../meetingManagement/components/TaskFilesDialog'
import {
  useMeetingManagementFontVars,
  useMeetingManagementTasks,
} from '../../meetingManagement/hooks/useMeetingManagement'
import { useTaskDecisionActions } from '../../meetingManagement/hooks/useTaskDecisionActions'
import { flattenTasks, type MeetingManagementTask } from '../../meetingManagement/types'
import { APPROVALS_PAGE_SIZE, TASK_CLOSURE_DEFAULT_SORT, TASK_CLOSURE_QUERY } from '../types'
import '../../meetings/meetings.css'
import '../../meetingManagement/meetingManagement.css'

/** "Kapat" onay penceresinin grubu — Toplantı Yönetimi'ninkinden AYRI (iki ekranın örneği karışmasın). */
const CLOSE_CONFIRM_GROUP = 'approvals-task-close'

/**
 * Onaylarım › <b>Görev Kapatma</b> sekmesi (131 US1): onaya gönderilmiş (Onay Bekleniyor) ana
 * görevlerin süzgeçsiz kuyruğu. Seçilen görevler tek işlemde <b>kapatılır</b> ya da tek tek yeni hedef
 * teslim tarihiyle <b>yeniden açılır</b>.
 *
 * <p>Karar, Toplantı Yönetimi (105) ile <b>aynı</b> uçlardan ve aynı bileşenlerle verilir (131
 * FR-016/FR-017/FR-018): satır bayrakları (`closable`/`reopenable`) sunucudan gelir, Kapat/Yeniden Aç
 * mantığı {@code useTaskDecisionActions}'ta, tablo/kartlar/pop-up'lar 105'in bileşenleridir. Bu sekme
 * yalnız sorguyu sabitler ve süzgeç çubuğunu kaldırır.
 */
export default function TaskClosureTab() {
  const { t } = useTranslation()
  const isCardView = useMediaQuery(CARD_VIEW_BREAKPOINT)
  const [first, setFirst] = useState(0)
  const [size, setSize] = useState(APPROVALS_PAGE_SIZE)
  const decision = useTaskDecisionActions(CLOSE_CONFIRM_GROUP)
  const { readOnly, selection, setSelection } = decision
  const [filesTarget, setFilesTarget] = useState<TaskFilesTarget | null>(null)
  const [detailTarget, setDetailTarget] = useState<TaskDetailTarget | null>(null)
  const openDetail = (row: MeetingManagementTask) =>
    setDetailTarget({ id: row.id, subTask: row.subTask })

  // Sıralama değişince ilk sayfaya dön. Kullanıcı kolon seçmediyse (ya da sıralamayı kaldırdıysa)
  // kuyruğun kendi düzeni geçerlidir: en uzun süredir bekleyen üstte.
  const sorting = useTableSort(() => setFirst(0))
  const list = useMeetingManagementTasks({
    ...TASK_CLOSURE_QUERY,
    page: Math.floor(first / size),
    size,
    sort: sorting.sort ?? TASK_CLOSURE_DEFAULT_SORT,
  })
  const fontVars = useMeetingManagementFontVars()

  const rows = useMemo(() => flattenTasks(list.data?.content ?? []), [list.data])
  const totalRecords = list.data?.page.totalElements ?? 0
  const emptyText = {
    title: t('approvals.empty.tasks.title'),
    description: t('approvals.empty.tasks.description'),
  }

  const onPage = (nextFirst: number, nextRows: number) => {
    setFirst(nextFirst)
    setSize(nextRows)
  }

  return (
    <div className="meetings-page approvals-task-closure" style={fontVars}>
      {/* Sayfa başlığı iki sekmece paylaşıldığı için işlem butonları sekmenin kendi araç satırında. */}
      {!readOnly && (
        <div className="approvals-toolbar">
          <TaskDecisionButtons actions={decision} />
        </div>
      )}

      {isCardView ? (
        <MeetingManagementCards
          rows={rows}
          loading={list.isLoading}
          totalRecords={totalRecords}
          first={first}
          rows_={size}
          onPage={onPage}
          selection={selection}
          onToggle={decision.toggleOne}
          selectable={!readOnly}
          onOpenFiles={(row) => setFilesTarget({ id: row.id, name: row.name })}
          onOpenDetail={openDetail}
          emptyText={emptyText}
        />
      ) : (
        <MeetingManagementTable
          rows={rows}
          loading={list.isLoading}
          totalRecords={totalRecords}
          first={first}
          rows_={size}
          onPage={onPage}
          sorting={sorting}
          selection={selection}
          onSelectionChange={setSelection}
          selectable={!readOnly}
          onOpenFiles={(row) => setFilesTarget({ id: row.id, name: row.name })}
          onOpenDetail={openDetail}
          emptyText={emptyText}
        />
      )}

      <ConfirmDialog group={CLOSE_CONFIRM_GROUP} />

      <ReopenTasksDialog
        visible={decision.reopenOpen}
        tasks={selection}
        onHide={() => {
          decision.closeReopen()
          list.refetch()
        }}
      />

      <TaskFilesDialog task={filesTarget} onHide={() => setFilesTarget(null)} />
      <TaskDetailDialog task={detailTarget} onHide={() => setDetailTarget(null)} />
    </div>
  )
}
